import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { api } from "@/lib/api";
import { readToken, clearToken } from "@/lib/tokenStore";

const BLOG_CATEGORIES = [
  "Erlebnisstandorte",
  "Hospitality & Guest Experience",
  "Digitale Markenräume",
  "Betreiberwissen",
  "Markt & Zukunft",
  "Developer & Content",
  "Partner & Sales",
];

const PRODUCT_OPTIONS = [
  "Allgemein",
  "PVRA",
  "AR_ENA",
  "PINE VR",
  "Hotel VR",
  "PINE Places",
  "Dev Programm",
  "Sales Partner",
];

const AUDIENCE_OPTIONS = [
  "Gründer",
  "Betreiber",
  "Hotels",
  "Eventlocations",
  "Entwickler",
  "Sales Partner",
  "Investoren",
  "Allgemein",
];

const POSTING_TYPES = ["Dev Programm", "Sales Partner", "Cooperation", "Content Partner", "Sonstige"];

const EMPTY = {
  type: "blog",
  title: "",
  slug: "",
  status: "draft",
  category: "",
  cover_image_url: "",
  author: "PINE Systems",
  published_at: "",
  excerpt: "",
  body: "",
  meta_title: "",
  meta_description: "",
  cta_text: "",
  cta_url: "",
  discord_url: "",
  product_focus: "Allgemein",
  audience: "Allgemein",
  posting_type: "",
  short_description: "",
  audience_target: "",
  requirements: "",
  pine_offers: "",
  process: "",
  compensation: "",
  expires_at: "",
  always_on: false,
};

export default function AdminContentEdit({ type }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = readToken();
  const [data, setData] = useState({ ...EMPTY, type });
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(id);

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    if (!id) return;
    api
      .get(`/admin/content/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData({ ...EMPTY, ...r.data }))
      .catch((e) => {
        if (e?.response?.status === 401) {
          clearToken();
          navigate("/admin");
        } else {
          toast.error("Laden fehlgeschlagen");
        }
      })
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!data.title.trim()) {
      toast.error("Titel ist erforderlich");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...data, type };
      if (isEdit) {
        await api.patch(`/admin/content/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await api.post("/admin/content", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      toast.success(isEdit ? "Gespeichert" : "Erstellt");
      navigate("/admin/content");
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        navigate("/admin");
      } else {
        toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-[#AEB7B7] p-10">Lädt…</p>;

  const isBlog = type === "blog";

  return (
    <div className="min-h-screen bg-[#050607] text-white">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <Link
          to="/admin/content"
          className="overline inline-flex items-center gap-2 hover:text-white transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> Content Editor
        </Link>
        <h1
          className="font-display text-3xl md:text-4xl uppercase mt-3"
          data-testid="content-edit-title"
        >
          {isEdit ? "Eintrag bearbeiten" : isBlog ? "Neuer Blogbeitrag" : "Neue Ausschreibung"}
        </h1>

        <form onSubmit={save} className="surface p-6 md:p-10 mt-10" data-testid="content-edit-form">
          {/* shared header */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="field-label">Titel *</label>
              <input
                required
                className="field"
                value={data.title}
                onChange={(e) => set("title", e.target.value)}
                data-testid="input-title"
              />
            </div>
            <div>
              <label className="field-label">Slug (leer = automatisch)</label>
              <input
                className="field font-mono"
                value={data.slug}
                onChange={(e) => set("slug", e.target.value)}
                data-testid="input-slug"
              />
            </div>
            <div>
              <label className="field-label">Status</label>
              <select
                className="field"
                value={data.status}
                onChange={(e) => set("status", e.target.value)}
                data-testid="select-status"
              >
                <option value="draft">Entwurf</option>
                <option value="published">Veröffentlicht</option>
              </select>
            </div>

            {isBlog && (
              <>
                <div>
                  <label className="field-label">Kategorie</label>
                  <select
                    className="field"
                    value={data.category}
                    onChange={(e) => set("category", e.target.value)}
                  >
                    <option value="">Bitte wählen</option>
                    {BLOG_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Autor</label>
                  <input
                    className="field"
                    value={data.author}
                    onChange={(e) => set("author", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label">Titelbild URL</label>
                  <input
                    className="field"
                    value={data.cover_image_url}
                    onChange={(e) => set("cover_image_url", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label">Produktbezug</label>
                  <select
                    className="field"
                    value={data.product_focus}
                    onChange={(e) => set("product_focus", e.target.value)}
                  >
                    {PRODUCT_OPTIONS.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Zielgruppe</label>
                  <select
                    className="field"
                    value={data.audience}
                    onChange={(e) => set("audience", e.target.value)}
                  >
                    {AUDIENCE_OPTIONS.map((a) => <option key={a}>{a}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Excerpt</label>
                  <textarea
                    className="field"
                    rows={3}
                    value={data.excerpt}
                    onChange={(e) => set("excerpt", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Artikelinhalt (Markdown / Plain)</label>
                  <textarea
                    className="field font-mono text-sm"
                    rows={14}
                    value={data.body}
                    onChange={(e) => set("body", e.target.value)}
                    data-testid="input-body"
                  />
                </div>
                <div>
                  <label className="field-label">Meta Title</label>
                  <input
                    className="field"
                    value={data.meta_title}
                    onChange={(e) => set("meta_title", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label">Meta Description</label>
                  <input
                    className="field"
                    value={data.meta_description}
                    onChange={(e) => set("meta_description", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label">CTA Button Text</label>
                  <input
                    className="field"
                    value={data.cta_text}
                    onChange={(e) => set("cta_text", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label">CTA URL</label>
                  <input
                    className="field"
                    value={data.cta_url}
                    onChange={(e) => set("cta_url", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Discord-Link (optional)</label>
                  <input
                    className="field"
                    value={data.discord_url}
                    onChange={(e) => set("discord_url", e.target.value)}
                  />
                </div>
              </>
            )}

            {!isBlog && (
              <>
                <div>
                  <label className="field-label">Typ</label>
                  <select
                    className="field"
                    value={data.posting_type}
                    onChange={(e) => set("posting_type", e.target.value)}
                  >
                    <option value="">Bitte wählen</option>
                    {POSTING_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label">Dauerhaft aktiv</label>
                  <select
                    className="field"
                    value={data.always_on ? "yes" : "no"}
                    onChange={(e) => set("always_on", e.target.value === "yes")}
                  >
                    <option value="no">Nein</option>
                    <option value="yes">Ja</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Kurzbeschreibung</label>
                  <textarea
                    className="field"
                    rows={3}
                    value={data.short_description}
                    onChange={(e) => set("short_description", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Zielgruppe</label>
                  <input
                    className="field"
                    value={data.audience_target}
                    onChange={(e) => set("audience_target", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Anforderungen</label>
                  <textarea
                    className="field"
                    rows={3}
                    value={data.requirements}
                    onChange={(e) => set("requirements", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Was PINE bietet</label>
                  <textarea
                    className="field"
                    rows={3}
                    value={data.pine_offers}
                    onChange={(e) => set("pine_offers", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Ablauf</label>
                  <textarea
                    className="field"
                    rows={3}
                    value={data.process}
                    onChange={(e) => set("process", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Vergütung / Konditionen</label>
                  <textarea
                    className="field"
                    rows={2}
                    value={data.compensation}
                    onChange={(e) => set("compensation", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label">CTA Text</label>
                  <input
                    className="field"
                    value={data.cta_text}
                    onChange={(e) => set("cta_text", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label">CTA URL</label>
                  <input
                    className="field"
                    value={data.cta_url}
                    onChange={(e) => set("cta_url", e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">Discord-Link (optional)</label>
                  <input
                    className="field"
                    value={data.discord_url}
                    onChange={(e) => set("discord_url", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label">Ablaufdatum (optional, ISO)</label>
                  <input
                    className="field"
                    value={data.expires_at}
                    onChange={(e) => set("expires_at", e.target.value)}
                  />
                </div>
              </>
            )}
          </div>

          <div className="hairline my-8" />

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary disabled:opacity-50"
              data-testid="content-save"
            >
              <Save size={14} strokeWidth={1.5} />
              {saving ? "Speichern…" : isEdit ? "Speichern" : "Erstellen"}
            </button>
            <Link to="/admin/content" className="btn-secondary">
              Abbrechen
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
